// `grid vault rotate <key>` — #1766. Replace the value of an EXISTING vault
// item. Rotate is deliberately distinct from `set`: it refuses to create, so a
// typo'd key fails loudly instead of silently minting a second item.
//
// There is no dedicated rotate endpoint — rotation is a PATCH { value } on the
// item, guarded by an existence pre-check. The value is write-only: read from
// --value-stdin or a hidden prompt, never from argv, never echoed.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { addGridOption, gridSlug, type GridOpts } from '../../grid-opt.js';
import {
  announceGridTarget,
  findVaultItemByKey,
  readSecretValue,
  resolveVaultGrid,
  vaultItemPath,
} from './shared.js';

export interface RunVaultRotateOpts {
  client: IV2Client;
  key: string;
  gridOverride: string | undefined;
  /** Scope to look the key up in. Defaults to `org`. */
  scope?: string;
  /** Read the value from stdin instead of prompting. */
  valueStdin?: boolean;
  /** Test seam: injected hidden-prompt reader. */
  readValue?: () => Promise<string>;
  /** Test seam: injected stdin reader. */
  readStdin?: () => Promise<string>;
}

export async function runVaultRotate(opts: RunVaultRotateOpts): Promise<void> {
  const scope = opts.scope ?? 'org';

  const grid = await resolveVaultGrid(opts.client, opts.gridOverride);
  announceGridTarget(grid);

  const value = await readSecretValue({
    valueStdin: opts.valueStdin,
    readValue: opts.readValue,
    readStdin: opts.readStdin,
  });

  const existing = await findVaultItemByKey(opts.client, grid.gridId, opts.key, scope);
  if (!existing) {
    throw new V2ApiError(
      404,
      'NOT_FOUND',
      `No vault item "${opts.key}" in ${grid.slug} (scope ${scope}). Use \`grid vault set\` to create it.`,
    );
  }

  await opts.client.patch(vaultItemPath(grid.gridId, existing.item_id), { value });
  process.stdout.write(`Rotated ${opts.key} in ${grid.slug}.\n`);
}

export const vaultRotateCommand: Command = addGridOption(
  new Command('rotate')
    .description('Replace the value of an existing vault item (the value is read out-of-band)')
    .argument('<key>', 'Item key to rotate (must already exist)')
    .option('--scope <scope>', 'Scope: org | personal', 'org')
    .option('--value-stdin', 'Read the value from stdin instead of prompting'),
  'Grid to rotate in (defaults to the active grid)',
).action(async (
  key: string,
  opts: GridOpts & { scope?: string; valueStdin?: boolean },
) => {
  try {
    const gridOverride = gridSlug(opts);
    const client = await getV2Client(gridOverride ?? undefined);
    await runVaultRotate({
      client,
      key,
      gridOverride,
      scope: opts.scope,
      valueStdin: opts.valueStdin,
    });
  } catch (err) {
    await handleV2Error(err);
  }
});
